import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ADMIN_WALLET = "ADJkonbgxndjbyL5XHfBLkVhEGiTUBChK9YMg8PtmPKa";

export type Admin2FAStatus = "idle" | "sending" | "pending" | "verifying" | "verified" | "error";

export function useAdmin2FA() {
  const [status, setStatus] = useState<Admin2FAStatus>("idle");
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdminWallet = useCallback((walletAddress: string) => {
    return walletAddress === ADMIN_WALLET;
  }, []);

  const send2FACode = useCallback(async (walletAddress: string): Promise<boolean> => {
    if (!isAdminWallet(walletAddress)) {
      return true; // Non-admin wallets don't need 2FA
    }

    setStatus("sending");
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-2fa-send", {
        body: { walletAddress },
      });

      if (fnError) {
        console.error("[2FA] Send error:", fnError);
        setError(fnError.message || "Failed to send verification code");
        setStatus("error");
        toast.error("Failed to send 2FA code");
        return false;
      }

      if (data?.error) {
        setError(data.error);
        setStatus("error");
        toast.error(data.error);
        return false;
      }

      if (data?.expiresAt) {
        setExpiresAt(new Date(data.expiresAt));
      }

      setStatus("pending");
      toast.success("Verification code sent to admin email");
      return true;
    } catch (e) {
      console.error("[2FA] Send exception:", e);
      setError("Network error. Please try again.");
      setStatus("error");
      return false;
    }
  }, [isAdminWallet]);

  const verify2FACode = useCallback(async (walletAddress: string, code: string): Promise<boolean> => {
    if (!isAdminWallet(walletAddress)) {
      return true; // Non-admin wallets don't need 2FA
    }

    if (!code || code.length !== 6) {
      setError("Please enter a 6-digit code");
      return false;
    }

    setStatus("verifying");
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("admin-2fa-verify", {
        body: { walletAddress, code },
      });

      if (fnError) {
        console.error("[2FA] Verify error:", fnError);
        setError("Invalid or expired code");
        setStatus("pending");
        toast.error("Invalid or expired code");
        return false;
      }

      if (data?.error) {
        setError(data.error);
        setStatus("pending");
        toast.error(data.error);
        return false;
      }

      if (data?.verified) {
        setStatus("verified");
        toast.success("2FA verified successfully");
        return true;
      }

      setError("Verification failed");
      setStatus("pending");
      return false;
    } catch (e) {
      console.error("[2FA] Verify exception:", e);
      setError("Network error. Please try again.");
      setStatus("pending");
      return false;
    }
  }, [isAdminWallet]);

  const reset = useCallback(() => {
    setStatus("idle");
    setExpiresAt(null);
    setError(null);
  }, []);

  return {
    status,
    expiresAt,
    error,
    isAdminWallet,
    send2FACode,
    verify2FACode,
    reset,
  };
}
